import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { STATUS_ORDER, bookedForLine, clientLabel, statusDot, statusStyle } from "./diaryShared";
import { formatTime } from "../../services/datetime";
import { labelFor } from "../../services/enumLabels";

/**
 * The diary's status furniture: the badge on every block, the legend, the
 * filter above the grid, and the hover card. One file so the four cannot
 * disagree about what a status looks like or is called.
 */

/**
 * A status pill. `compact` is the one that sits inside a grid block next to
 * the client's name — smaller type, and it truncates before the name does
 * not. `dotOnly` is for a lane too narrow to hold a word; the word is still
 * there for screen readers.
 */
export function StatusBadge({ status, compact = false, dotOnly = false, className = "" }) {
  useTranslation(); // re-render on a language switch: the label is read at render
  const label = labelFor("booking_status", status);

  if (dotOnly) {
    return (
      <span className={`inline-flex flex-shrink-0 items-center ${className}`}>
        <span aria-hidden="true" className={`h-2 w-2 rounded-full ${statusDot(status)}`} />
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  return (
    <span
      className={`inline-flex max-w-full flex-shrink-0 items-center gap-1 truncate rounded-full border font-medium ${
        compact ? "px-1.5 text-[10px] leading-4" : "px-2 py-0.5 text-caption"
      } ${statusStyle(status)} ${className}`}
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${statusDot(status)}`} />
      <span className="truncate">{label}</span>
    </span>
  );
}

/** A colour nobody can decode is decoration — so the key is always on screen. */
export function StatusLegend() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-caption text-ink-2">
      <span className="font-medium text-ink">{t("diary.legend")}</span>
      {STATUS_ORDER.map((status) => (
        <span key={status} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-sm ${statusDot(status)}`} />
          {labelFor("booking_status", status)}
        </span>
      ))}
    </div>
  );
}

/**
 * Multi-select status chips with a count each, plus "Hide cancelled".
 *
 * `selected` is the list in the URL, where **empty means every status** — the
 * default, and what a link without the parameter should show. A choice that
 * comes back round to all seven is written as empty again, and the last chip
 * cannot be switched off: a diary filtered down to nothing answers no question.
 *
 * "Hide cancelled" is not a second piece of state; it is the cancelled chip,
 * offered as a switch because it is the one people reach for. The two can
 * therefore never disagree.
 */
export function StatusFilter({ selected, counts, onChange }) {
  const { t, i18n } = useTranslation();
  const active = selected.length ? selected : STATUS_ORDER;
  const number = (n) => new Intl.NumberFormat(i18n.language).format(n ?? 0);

  const commit = (next) => {
    const ordered = STATUS_ORDER.filter((s) => next.includes(s));
    if (ordered.length === 0 || ordered.length === STATUS_ORDER.length) onChange([]);
    else onChange(ordered);
  };

  const toggle = (status) =>
    commit(active.includes(status) ? active.filter((s) => s !== status) : [...active, status]);

  const hidingCancelled = !active.includes("cancelled");

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-caption font-medium text-ink-2">{t("diary.statusFilter")}</span>

      <div
        role="group"
        aria-label={t("diary.statusFilter")}
        className="-mx-1 flex max-w-full gap-1.5 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0"
      >
        <button
          type="button"
          onClick={() => onChange([])}
          aria-pressed={selected.length === 0}
          className={`h-8 flex-shrink-0 rounded-full border px-3 text-caption font-medium transition-colors ${
            selected.length === 0
              ? "border-accent bg-accent-wash text-accent-text"
              : "border-line bg-surface text-ink-2 hover:border-line-strong"
          }`}
        >
          {t("diary.allStatuses")}
        </button>

        {STATUS_ORDER.map((status) => {
          const on = active.includes(status);

          return (
            <button
              key={status}
              type="button"
              onClick={() => toggle(status)}
              aria-pressed={on}
              className={`inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-full border px-3 text-caption font-medium transition-colors ${
                on
                  ? statusStyle(status)
                  : "border-line bg-surface text-ink-2 opacity-70 hover:border-line-strong hover:opacity-100"
              }`}
            >
              <span aria-hidden="true" className={`h-2 w-2 rounded-full ${statusDot(status)}`} />
              {labelFor("booking_status", status)}
              <span className="tabular-nums opacity-80">{number(counts?.[status])}</span>
            </button>
          );
        })}
      </div>

      <label className="ms-auto inline-flex cursor-pointer select-none items-center gap-2 text-caption text-ink-2">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-line-strong accent-[var(--pl-accent)]"
          checked={hidingCancelled}
          onChange={() => toggle("cancelled")}
        />
        {t("diary.hideCancelled")}
      </label>

      {/* Back to the default — every status, nothing hidden. Branch, date and
          view are where you are, not filters, so they stay put. Disabled
          rather than hidden, so the bar does not shift as chips change. */}
      <button
        type="button"
        onClick={() => onChange([])}
        disabled={selected.length === 0}
        className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-full border border-line-strong bg-surface px-3 text-caption font-medium text-ink transition-colors hover:bg-raised disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface"
      >
        {t("diary.resetFilters")}
      </button>
    </div>
  );
}

/**
 * Hover / focus card for a booking.
 *
 * Grid blocks are two truncated lines at best, so the card is where the rest
 * lives: time, client, services, providers, status and price. Rendered into
 * `document.body` with fixed positioning, because every grid sits inside
 * `overflow-hidden` / `overflow-x-auto` containers that would clip it.
 *
 * Keyboard users get it on focus, and the block's own label carries the same
 * facts, so nothing here is hover-only.
 */
export function useHoverCard({ disabled = false } = {}) {
  const [card, setCard] = useState(null);

  const hide = useCallback(() => setCard(null), []);

  const bind = useCallback(
    (appointment) => ({
      onMouseEnter: (e) =>
        !disabled && setCard({ appointment, rect: e.currentTarget.getBoundingClientRect() }),
      onMouseLeave: hide,
      onFocus: (e) => !disabled && setCard({ appointment, rect: e.currentTarget.getBoundingClientRect() }),
      onBlur: hide,
    }),
    [disabled, hide]
  );

  return { card: disabled ? null : card, bind, hide };
}

const CARD_WIDTH = 272;
const CARD_HEIGHT = 200;

export function AppointmentHoverCard({ card }) {
  const { t } = useTranslation();
  if (!card) return null;

  const { appointment: a, rect } = card;
  const rtl = document.documentElement.dir === "rtl";

  // Beside the block's start edge, flipped above when the bottom is close.
  const left = Math.min(
    Math.max(8, rtl ? rect.right - CARD_WIDTH : rect.left),
    window.innerWidth - CARD_WIDTH - 8
  );
  const top =
    rect.bottom + 6 + CARD_HEIGHT < window.innerHeight
      ? rect.bottom + 6
      : Math.max(8, rect.top - CARD_HEIGHT - 6);

  const services = (a.services ?? []).map((s) => s.service?.name).filter(Boolean);
  const providers = [...new Set((a.services ?? []).map((s) => s.staff?.display_name).filter(Boolean))];

  const row = (label, value, opts = {}) => (
    <div className="flex gap-3 py-0.5">
      <dt className="w-20 flex-shrink-0 text-ink-2">{label}</dt>
      <dd className={`min-w-0 flex-1 break-words text-ink ${opts.className ?? ""}`} dir={opts.dir}>
        {value}
      </dd>
    </div>
  );

  return createPortal(
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 rounded-lg border border-line bg-overlay p-3 text-caption shadow-e2"
      style={{ top, left, width: CARD_WIDTH }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-ink">{clientLabel(t, a)}</span>
        <StatusBadge status={a.status} />
      </div>
      <dl>
        {row(t("diary.hover.time"), `${formatTime(a.local_starts_at)} – ${formatTime(a.local_ends_at)}`, {
          dir: "ltr",
          className: "text-start tabular-nums",
        })}
        {bookedForLine(t, a) ? row(t("diary.hover.bookedFor"), bookedForLine(t, a)) : null}
        {row(t("diary.hover.service"), services.join(", ") || "—")}
        {row(t("diary.hover.provider"), providers.join(", ") || t("diary.hover.unassigned"))}
        {row(t("diary.hover.price"), a.total != null ? `${a.total} ${a.currency?.code ?? ""}`.trim() : "—", {
          dir: "ltr",
          className: "text-start tabular-nums",
        })}
        {a.status === "cancelled" && a.cancellation_reason
          ? row(t("diary.hover.reason"), a.cancellation_reason)
          : null}
      </dl>
    </div>,
    document.body
  );
}

/** One readable sentence for a block's accessible name. */
export const blockLabel = (t, a) =>
  [
    clientLabel(t, a),
    `${formatTime(a.local_starts_at)}–${formatTime(a.local_ends_at)}`,
    (a.services ?? [])
      .map((s) => s.service?.name)
      .filter(Boolean)
      .join(", "),
    labelFor("booking_status", a.status),
  ]
    .filter(Boolean)
    .join(" · ");
